<template>
  <div
    id="app"
    class="font-sans text-dark bg-light antialiased overflow-x-hidden"
  >
    <!-- 导航栏 - 滚动时变化样式 -->
    <header
      id="navbar"
      class="fixed w-full top-0 z-50 transition-all duration-300 bg-transparent"
    >
      <div class="container mx-auto px-4 sm:px-6 lg:px-8">
        <div class="flex justify-between items-center py-4">
          <!-- Logo -->
          <a
            href="#"
            class="flex items-center space-x-2 px-3 py-1.5 rounded-md transition-all duration-300"
            :class="{
              'opacity-0 visibility-hidden': !navbarBgWhite,
              'opacity-100 visibility-visible': navbarBgWhite,
            }"
          >
            <img
              src="@/assets/logo2.png"
              class="h-10 w-auto object-contain"
              alt="logo"
            />
            <span class="text-[#0A4DA2] text-lg font-bold">{{
              langData.logoName
            }}</span>
          </a>
          <!-- 桌面端导航 -->
          <nav class="hidden md:flex space-x-10">
            <a
              href="#home"
              class="text-white text-base hover:text-secondary transition-colors font-medium"
              @click.prevent="scrollToSection('home')"
              >{{ langData.nav.home }}</a
            >
            <a
              href="#about"
              class="text-white text-base hover:text-secondary transition-colors font-medium"
              @click.prevent="scrollToSection('about')"
              >{{ langData.nav.about }}</a
            >
            <a
              href="#services"
              class="text-white text-base hover:text-secondary transition-colors font-medium"
              @click.prevent="scrollToSection('services')"
              >{{ langData.nav.services }}</a
            >
            <a
              href="#cases"
              class="text-white text-base hover:text-secondary transition-colors font-medium"
              @click.prevent="scrollToSection('cases')"
              >{{ langData.nav.cases }}</a
            >
            <a
              href="#contact"
              class="text-white text-base hover:text-secondary transition-colors font-medium"
              @click.prevent="scrollToSection('contact')"
              >{{ langData.nav.contact }}</a
            >
          </nav>

          <!-- 桌面端：立即咨询 + 语言切换 组合 -->
          <div class="hidden md:flex items-center space-x-3">
            <button
              class="bg-secondary hover:bg-secondary/90 text-white px-6 py-2 rounded-full transition-all hover:shadow-lg"
              @click="goConsult"
            >
              {{ langData.btn.consult }}
            </button>
            <!-- 语言切换按钮 -->
            <button
              @click="toggleLang"
              class="bg-secondary hover:bg-secondary/90 text-white px-7 py-2 rounded-full transition-all hover:shadow-lg"
            >
              {{ currentLang === "zh" ? "EN" : "简体" }}
            </button>
          </div>

          <!-- 移动端菜单按钮 -->
          <button
            id="menuBtn"
            class="md:hidden text-white text-2xl"
            @click="toggleMobileMenu"
            :class="{ 'text-dark': navbarBgWhite }"
          >
            <i class="fa fa-bars"></i>
          </button>
        </div>
      </div>

      <!-- 移动端导航菜单 -->
      <div
        id="mobileMenu"
        class="md:hidden"
        :class="{ hidden: !isMobileMenuOpen }"
      >
        <div class="bg-white shadow-lg absolute w-full">
          <div class="container mx-auto px-4 py-4 flex flex-col space-y-4">
            <a
              href="#home"
              class="py-2 px-4 text-base hover:bg-gray-100 rounded transition-colors"
              @click.prevent="scrollToSection('home')"
              >{{ langData.nav.home }}</a
            >
            <a
              href="#about"
              class="py-2 px-4 text-base hover:bg-gray-100 rounded transition-colors"
              @click.prevent="scrollToSection('about')"
              >{{ langData.nav.about }}</a
            >
            <a
              href="#services"
              class="py-2 px-4 text-base hover:bg-gray-100 rounded transition-colors"
              @click.prevent="scrollToSection('services')"
              >{{ langData.nav.services }}</a
            >
            <a
              href="#cases"
              class="py-2 px-4 text-base hover:bg-gray-100 rounded transition-colors"
              @click.prevent="scrollToSection('cases')"
              >{{ langData.nav.cases }}</a
            >
            <a
              href="#contact"
              class="py-2 px-4 text-base hover:bg-gray-100 rounded transition-colors"
              @click.prevent="scrollToSection('contact')"
              >{{ langData.nav.contact }}</a
            >
            <!-- 移动端：立即咨询 + 语言切换 组合 -->
            <div class="flex items-center justify-center space-x-3">
              <a
                href="https://work.weixin.qq.com/kfid/kfc964776c9b0909cb6"
                target="_blank"
                class="bg-secondary text-white py-2 px-6 text-base rounded-full text-center w-2/5"
              >
                {{ langData.btn.consult }}
              </a>
              <!-- 移动端语言切换按钮 -->
              <button
                @click="toggleLang"
                class="text-dark hover:text-secondary border border-gray-200 px-3 py-2 rounded-full transition-colors hover:bg-gray-100 w-1/5"
              >
                {{ currentLang === "zh" ? "EN" : "简体" }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </header>

    <!-- Hero区域 -->
    <section
      id="home"
      class="relative h-screen flex items-center justify-center overflow-hidden"
    >
      <div class="absolute inset-0 bg-gradient-tech opacity-90 z-0"></div>
      <div
        class="absolute inset-0 bg-[url('https://picsum.photos/id/180/1920/1080')] bg-cover bg-center bg-no-repeat mix-blend-overlay opacity-20 z-0"
      ></div>
      <div
        class="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10 text-center animate-fade-in"
      >
        <h1
          class="text-[clamp(2.5rem,5vw,4.5rem)] font-bold text-white leading-tight text-shadow mb-6"
        >
          {{ langData.hero.title }}
        </h1>
        <p
          class="text-[clamp(1rem,2vw,1.25rem)] text-white/90 max-w-3xl mx-auto mb-14"
        >
          {{ langData.hero.desc }}
        </p>
        <div class="flex flex-col sm:flex-row justify-center gap-4">
          <a
            href="#services"
            class="bg-white text-primary px-8 py-3 rounded-full font-medium hover:bg-gray-100 transition-all shadow-lg hover:shadow-xl flex items-center justify-center"
            @click.prevent="scrollToSection('services')"
          >
            {{ langData.btn.service }}
          </a>
          <a
            href="#contact"
            class="bg-transparent border-2 border-white text-white px-8 py-3 rounded-full font-medium hover:bg-white/10 transition-all flex items-center justify-center"
            @click.prevent="scrollToSection('contact')"
          >
            {{ langData.nav.contact }}
          </a>
        </div>
        <!-- <div class="absolute bottom-10 left-1/2 transform -translate-x-1/2 animate-bounce">
          <a href="#about" class="text-white/80 hover:text-white" @click.prevent="scrollToSection('about')">
            <i class="fa fa-chevron-down text-2xl"></i>
          </a>
        </div> -->
      </div>
    </section>

    <!-- 关于我们 -->
    <section id="about" class="py-20 bg-white">
      <div class="container mx-auto px-4 sm:px-6 lg:px-8">
        <div class="text-center mb-16 animate-slide-up">
          <h2 class="text-[clamp(1.8rem,3vw,2.5rem)] font-bold text-dark mb-4">
            {{ langData.about.title }}
          </h2>
          <div class="w-20 h-1 bg-secondary mx-auto mb-6"></div>
          <p class="text-gray-600 max-w-2xl mx-auto">
            {{ langData.about.brief }}
          </p>
        </div>
        <div class="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
          <div class="hover-lift">
            <img
              src="@/assets/lo.png"
              class="w-full max-w-full h-auto rounded-xl shadow-lg"
            />
          </div>
          <div class="space-y-6 animate-slide-up" style="animation-delay: 0.2s">
            <h3 class="text-2xl font-bold text-primary">
              {{ langData.about.profileTitle }}
            </h3>
            <p class="text-gray-600">
              {{ langData.about.profileDesc }}
            </p>
            <h3 class="text-2xl font-bold text-primary">
              {{ langData.about.missionTitle }}
            </h3>
            <p class="text-gray-600">
              {{ langData.about.missionDesc }}
            </p>
            <br />
            <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div class="flex items-start space-x-3">
                <div class="w-6 flex-shrink-0 flex items-center justify-center">
                  <i class="fa fa-lightbulb-o text-secondary text-2xl"></i>
                </div>
                <div>
                  <h4 class="font-medium">
                    {{ langData.about.advantage1.title }}
                  </h4>
                  <p class="text-gray-600 text-sm">
                    {{ langData.about.advantage1.desc }}
                  </p>
                </div>
              </div>
              <div class="flex items-start space-x-3">
                <div class="w-6 flex-shrink-0 flex items-center justify-center">
                  <i class="fa fa-handshake-o text-secondary text-xl"></i>
                </div>
                <div>
                  <h4 class="font-medium">
                    {{ langData.about.advantage2.title }}
                  </h4>
                  <p class="text-gray-600 text-sm">
                    {{ langData.about.advantage2.desc }}
                  </p>
                </div>
              </div>
              <div class="flex items-start space-x-3">
                <div class="w-6 flex-shrink-0 flex items-center justify-center">
                  <i class="fa fa-users text-secondary text-xl"></i>
                </div>
                <div>
                  <h4 class="font-medium">
                    {{ langData.about.advantage3.title }}
                  </h4>
                  <p class="text-gray-600 text-sm">
                    {{ langData.about.advantage3.desc }}
                  </p>
                </div>
              </div>
              <div class="flex items-start space-x-3">
                <div class="w-6 flex-shrink-0 flex items-center justify-center">
                  <i class="fa fa-star text-secondary text-2xl"></i>
                </div>
                <div>
                  <h4 class="font-medium">
                    {{ langData.about.advantage4.title }}
                  </h4>
                  <p class="text-gray-600 text-sm">
                    {{ langData.about.advantage4.desc }}
                  </p>
                </div>
              </div>
            </div>
            <div class="grid grid-cols-3 gap-14 mt-8">
              <div class="text-center">
                <span class="block text-4xl font-bold text-secondary">15+</span>
                <span class="text-gray-600 text-sm">{{
                  langData.about.data1
                }}</span>
              </div>
              <div class="text-center">
                <span class="block text-4xl font-bold text-secondary">20+</span>
                <span class="text-gray-600 text-sm">{{
                  langData.about.data2
                }}</span>
              </div>
              <div class="text-center">
                <span class="block text-4xl font-bold text-secondary">30+</span>
                <span class="text-gray-600 text-sm">{{
                  langData.about.data3
                }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- 产品服务 -->
    <section id="services" class="py-20 bg-gray-100">
      <div class="container mx-auto px-4 sm:px-6 lg:px-8">
        <div class="text-center mb-16 animate-slide-up">
          <h2 class="text-[clamp(1.8rem,3vw,2.5rem)] font-bold text-dark mb-4">
            {{ langData.services.title }}
          </h2>
          <div class="w-20 h-1 bg-secondary mx-auto mb-6"></div>
          <p class="text-gray-600 max-w-2xl mx-auto">
            {{ langData.services.brief }}
          </p>
        </div>
        <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-8">
          <div
            class="rounded-xl shadow-md p-8 hover-lift animate-slide-up productBgImgOne"
            style="animation-delay: 0.1s"
            @click="productDetails(1)"
          >
            <div class="flex items-center gap-4 mb-4 w-full">
              <div
                class="w-14 h-14 bg-secondary/10 rounded-full flex items-center justify-center flex-shrink-0"
              >
                <i class="fa fa-cloud text-secondary text-2xl"></i>
              </div>
              <h3 class="text-2xl font-bold m-0">
                {{ langData.services.item1.title }}
              </h3>
            </div>
          </div>
          <div
            class="rounded-xl shadow-md p-8 hover-lift animate-slide-up productBgImgTwo"
            style="animation-delay: 0.2s"
            @click="productDetails(2)"
          >
            <div class="flex items-center gap-4 mb-4 w-full">
              <div
                class="w-14 h-14 bg-secondary/10 rounded-full flex items-center justify-center flex-shrink-0"
              >
                <i class="fa fa-brain text-secondary text-2xl"></i>
              </div>
              <h3 class="text-2xl font-bold m-0">
                {{ langData.services.item2.title }}
              </h3>
            </div>
          </div>
          <div
            class="rounded-xl shadow-md p-8 hover-lift animate-slide-up productBgImgThree"
            style="animation-delay: 0.3s"
            @click="productDetails(3)"
          >
            <div class="flex items-center gap-4 mb-4 w-full">
              <div
                class="w-14 h-14 bg-secondary/10 rounded-full flex items-center justify-center flex-shrink-0"
              >
                <i class="fa fa-shield text-secondary text-2xl"></i>
              </div>
              <h3 class="text-2xl font-bold m-0">
                {{ langData.services.item3.title }}
              </h3>
            </div>
          </div>
          <div
            class="rounded-xl shadow-md p-8 hover-lift animate-slide-up productBgImgFour"
            style="animation-delay: 0.4s"
            @click="productDetails(4)"
          >
            <div class="flex items-center gap-4 mb-4 w-full">
              <div
                class="w-14 h-14 bg-secondary/10 rounded-full flex items-center justify-center flex-shrink-0"
              >
                <i class="fa fa-database text-secondary text-2xl"></i>
              </div>
              <h3 class="text-2xl font-bold m-0">
                {{ langData.services.item4.title }}
              </h3>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- 成功案例 -->
    <section id="cases" class="py-20 bg-white">
      <div class="container mx-auto px-4 sm:px-6 lg:px-8">
        <div class="text-center mb-16 animate-slide-up">
          <h2 class="text-[clamp(1.8rem,3vw,2.5rem)] font-bold text-dark mb-4">
            {{ langData.cases.title }}
          </h2>
          <div class="w-20 h-1 bg-secondary mx-auto mb-6"></div>
          <p class="text-gray-600 max-w-2xl mx-auto">
            {{ langData.cases.brief }}
          </p>
        </div>
        <div
          class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 items-stretch"
        >
          <!-- 案例1 -->
          <div
            class="rounded-xl overflow-hidden shadow-md hover-lift animate-slide-up flex flex-col"
            style="animation-delay: 0.1s"
            @click="caseDetails(1)"
          >
            <img
              src="@/assets/4.png"
              class="w-full h-56 object-cover flex-shrink-0"
            />
            <!-- 内容区Grid布局：标签+标题+内容容器（占剩余高度） -->
            <div
              class="p-6 bg-white grid grid-rows-[auto_auto_1fr] gap-3 flex-1"
            >
              <span
                class="text-xs text-secondary font-medium bg-secondary/10 px-3 py-1 rounded-full w-fit flex-shrink-0"
                >{{ langData.cases.tag1 }}</span
              >
              <!-- 标题区：Grid自动统一最高高度 -->
              <h3 class="text-xl font-bold leading-tight overflow-hidden">
                {{ langData.cases.item1.title }}
              </h3>
              <!-- 内容容器：占剩余高度，内部Flex让文字贴底部 -->
              <div class="flex flex-col justify-end overflow-hidden">
                <p class="text-gray-600 text-sm leading-relaxed">
                  {{ langData.cases.item1.desc }}
                </p>
              </div>
            </div>
          </div>
          <!-- 案例2 -->
          <div
            class="rounded-xl overflow-hidden shadow-md hover-lift animate-slide-up flex flex-col"
            style="animation-delay: 0.2s"
            @click="caseDetails(2)"
          >
            <img
              src="@/assets/1.png"
              class="w-full h-56 object-cover flex-shrink-0"
            />
            <div
              class="p-6 bg-white grid grid-rows-[auto_auto_1fr] gap-3 flex-1"
            >
              <span
                class="text-xs text-secondary font-medium bg-secondary/10 px-3 py-1 rounded-full w-fit flex-shrink-0"
                >{{ langData.cases.tag2 }}</span
              >
              <h3 class="text-xl font-bold leading-tight overflow-hidden">
                {{ langData.cases.item2.title }}
              </h3>
              <div class="flex flex-col justify-end overflow-hidden">
                <p class="text-gray-600 text-sm leading-relaxed">
                  {{ langData.cases.item2.desc }}
                </p>
              </div>
            </div>
          </div>
          <!-- 案例3 -->
          <div
            class="rounded-xl overflow-hidden shadow-md hover-lift animate-slide-up flex flex-col"
            style="animation-delay: 0.3s"
            @click="caseDetails(3)"
          >
            <img
              src="@/assets/3.png"
              class="w-full h-56 object-cover flex-shrink-0"
            />
            <div
              class="p-6 bg-white grid grid-rows-[auto_auto_1fr] gap-3 flex-1"
            >
              <span
                class="text-xs text-secondary font-medium bg-secondary/10 px-3 py-1 rounded-full w-fit flex-shrink-0"
                >{{ langData.cases.tag3 }}</span
              >
              <h3 class="text-xl font-bold leading-tight overflow-hidden">
                {{ langData.cases.item3.title }}
              </h3>
              <div class="flex flex-col justify-end overflow-hidden">
                <p class="text-gray-600 text-sm leading-relaxed">
                  {{ langData.cases.item3.desc }}
                </p>
              </div>
            </div>
          </div>
          <!-- 案例4 -->
          <div
            class="rounded-xl overflow-hidden shadow-md hover-lift animate-slide-up flex flex-col"
            style="animation-delay: 0.4s"
            @click="caseDetails(4)"
          >
            <img
              src="@/assets/2.png"
              class="w-full h-56 object-cover flex-shrink-0"
            />
            <div
              class="p-6 bg-white grid grid-rows-[auto_auto_1fr] gap-3 flex-1"
            >
              <span
                class="text-xs text-secondary font-medium bg-secondary/10 px-3 py-1 rounded-full w-fit flex-shrink-0"
                >{{ langData.cases.tag4 }}</span
              >
              <h3 class="text-xl font-bold leading-tight overflow-hidden">
                {{ langData.cases.item4.title }}
              </h3>
              <div class="flex flex-col justify-end overflow-hidden">
                <p class="text-gray-600 text-sm leading-relaxed">
                  {{ langData.cases.item4.desc }}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- 联系我们 -->
    <section id="contact" class="py-20 bg-gray-100">
      <div class="container mx-auto px-4 sm:px-6 lg:px-8">
        <div class="text-center mb-10 animate-slide-up">
          <h2 class="text-[clamp(1.8rem,3vw,2.5rem)] font-bold text-dark mb-4">
            {{ langData.contact.title }}
          </h2>
          <div class="w-20 h-1 bg-secondary mx-auto mb-6"></div>
          <p class="text-gray-600 max-w-2xl mx-auto">
            {{ langData.contact.brief }}
          </p>
        </div>
        <div class="max-w-7xl mx-auto">
          <div class="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div
              class="bg-white border border-gray-200 rounded-xl shadow-md p-6 hover-lift flex flex-col items-center"
            >
              <div
                class="w-12 h-12 bg-secondary/10 rounded-full flex items-center justify-center flex-shrink-0 mb-3"
              >
                <i class="fa fa-map-marker text-secondary text-xl"></i>
              </div>
              <h4 class="font-bold mb-2 text-center">
                {{ langData.contact.addressTitle }}
              </h4>
              <p class="text-gray-600 w-full text-center">
                {{ langData.contact.addressDesc }}
              </p>
            </div>
            <div
              class="bg-white border border-gray-200 rounded-xl shadow-md p-6 hover-lift flex flex-col items-center"
            >
              <div
                class="w-12 h-12 bg-secondary/10 rounded-full flex items-center justify-center flex-shrink-0 mb-3"
              >
                <i class="fa fa-phone text-secondary text-xl"></i>
              </div>
              <h4 class="font-bold mb-2 text-center">
                {{ langData.contact.phoneTitle }}
              </h4>
              <p class="text-gray-600 w-full text-center">86-021-6045-5908</p>
            </div>
            <div
              class="bg-white border border-gray-200 rounded-xl shadow-md p-6 hover-lift flex flex-col items-center"
            >
              <div
                class="w-12 h-12 bg-secondary/10 rounded-full flex items-center justify-center flex-shrink-0 mb-3"
              >
                <i class="fa fa-envelope text-secondary text-xl"></i>
              </div>
              <h4 class="font-bold mb-2 text-center">
                {{ langData.contact.emailTitle }}
              </h4>
              <p class="text-gray-600 w-full text-center">
                Service@Inf-Health.com
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- 页脚 -->
    <footer class="bg-gray-900 text-white pt-16 pb-8">
      <div class="container mx-auto px-4 sm:px-6 lg:px-8">
        <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-12">
          <div>
            <h4 class="text-xl font-bold mb-6 text-white">
              {{ langData.footer.company }}
            </h4>
            <p class="text-gray-400 mb-6">
              {{ langData.footer.companyDesc }}
            </p>
            <div class="flex space-x-4">
              <div class="bg-white p-2 rounded-md">
                <img
                  src="@/assets/er.png"
                  class="w-24 h-24 object-cover max-w-full"
                />
              </div>
            </div>
          </div>
          <div>
            <h4 class="text-xl font-bold mb-6 text-white">
              {{ langData.footer.quickLink }}
            </h4>
            <ul class="space-y-3 text-gray-400">
              <li>
                <a
                  href="#home"
                  class="hover:text-white transition-colors"
                  @click.prevent="scrollToSection('home')"
                  >{{ langData.nav.home }}</a
                >
              </li>
              <li>
                <a
                  href="#about"
                  class="hover:text-white transition-colors"
                  @click.prevent="scrollToSection('about')"
                  >{{ langData.nav.about }}</a
                >
              </li>
              <li>
                <a
                  href="#services"
                  class="hover:text-white transition-colors"
                  @click.prevent="scrollToSection('services')"
                  >{{ langData.nav.services }}</a
                >
              </li>
              <li>
                <a
                  href="#cases"
                  class="hover:text-white transition-colors"
                  @click.prevent="scrollToSection('cases')"
                  >{{ langData.nav.cases }}</a
                >
              </li>
              <li>
                <a
                  href="#contact"
                  class="hover:text-white transition-colors"
                  @click.prevent="scrollToSection('contact')"
                  >{{ langData.nav.contact }}</a
                >
              </li>
            </ul>
          </div>
          <div>
            <h4 class="text-xl font-bold mb-6 text-white">
              {{ langData.footer.serviceScope }}
            </h4>
            <ul class="space-y-3 text-gray-400">
              <li>
                <a href="#" class="hover:text-white transition-colors">{{
                  langData.services.item1.title
                }}</a>
              </li>
              <li>
                <a href="#" class="hover:text-white transition-colors">{{
                  langData.services.item2.title
                }}</a>
              </li>
              <li>
                <a href="#" class="hover:text-white transition-colors">{{
                  langData.services.item3.title
                }}</a>
              </li>
              <li>
                <a href="#" class="hover:text-white transition-colors">{{
                  langData.services.item4.title
                }}</a>
              </li>
            </ul>
          </div>
          <div>
            <h4 class="text-xl font-bold mb-6 text-white">
              {{ langData.nav.contact }}
            </h4>
            <ul class="space-y-3 text-gray-400">
              <li class="flex items-start">
                <i class="fa fa-map-marker mt-1 mr-3"></i>
                <span>{{ langData.contact.addressDesc }}</span>
              </li>
              <li class="flex items-start">
                <i class="fa fa-phone mt-1 mr-3"></i>
                <span>86-021-6045-5908</span>
              </li>
              <li class="flex items-start">
                <i class="fa fa-envelope mt-1 mr-3"></i>
                <span>Service@Inf-Health.com</span>
              </li>
              <li class="flex items-start">
                <i class="fa fa-clock-o mt-1 mr-3"></i>
                <span>{{ langData.footer.workTime }}</span>
              </li>
            </ul>
          </div>
        </div>
        <!-- 备案号保留原文字，不翻译 -->
        <div
          class="border-t border-gray-800 pt-8 text-center text-gray-500 text-sm"
        >
          <div class="flex items-center justify-center mb-1">
            <img
              src="https://tech001.oss-cn-hangzhou.aliyuncs.com/%E5%85%AC%E5%AE%89%E5%A4%87%E6%A1%88.png"
              class="w-5 h-5 mr-1 max-w-full"
            />
            <a
              href="http://beian.miit.gov.cn/"
              target="_blank"
              class="text-white"
            >
              沪公网安备31010402334052号
            </a>
          </div>
          <span class="text-white">【 沪ICP备2023002880号 © 中弘润景】</span>
        </div>
      </div>
    </footer>
  </div>
</template>

<script setup>
import { ref, onMounted, computed } from "vue";
import { Button } from "ant-design-vue";
import { useRouter, useRoute } from "vue-router";
const router = useRouter();
const route = useRoute();

// 1. 移动端菜单状态
const isMobileMenuOpen = ref(false);
// 2. 语言状态：默认简体中文(zh)，可切换为英文(en)
const currentLang = ref(localStorage.getItem("siteLang") || "zh");
// 3. 导航栏背景是否变白（用于语言按钮文字颜色适配）
const navbarBgWhite = ref(false);
// 优先读取本地存储，无则默认中文

// 切换移动端菜单
const toggleMobileMenu = () => {
  isMobileMenuOpen.value = !isMobileMenuOpen.value;
};
// 跳转到客服
const goConsult = () => {
  window.open(
    "https://work.weixin.qq.com/kfid/kfc964776c9b0909cb6",
    "_blank",
    "noopener noreferrer",
  );
};
// 4. 核心：双语文本映射对象（所有页面文本都在这里，按模块分类，便于维护）
const languageMap = {
  // 简体中文
  zh: {
    logoName: "中弘润景科技",
    nav: {
      home: "首页",
      about: "关于我们",
      services: "产品服务",
      cases: "成功案例",
      contact: "联系我们",
    },
    btn: { consult: "立即咨询", service: "了解产品服务" },
    hero: {
      title: "科技赋能产业升级 · 数据驱动业务增长",
      desc: "中弘润景科技专注于数字化解决方案、人工智能应用与企业级技术服务，助力企业实现数字化转型与高质量发展",
    },
    about: {
      title: "关于中弘润景",
      brief:
        "中弘润景成立于2019年，是一家以数字化中台与智能化系统为核心能力的科技型企业。公司聚焦金融科技、保险科技、物流与教育等场景，通过“系统建设 + 场景运营 + 数据智能”三位一体模式，帮助企业实现业务在线化、数据资产化与运营智能化。",
      profileTitle: "公司概况",
      profileDesc:
        "中弘润景总部位于上海，在浙江、江苏、广东等地设有分支机构。公司以技术创新为核心驱动力，持续输出稳定、安全、可扩展的行业级数字化解决方案。",
      missionTitle: "企业使命",
      missionDesc:
        "以科技赋能产业升级，以数据驱动业务增长，让系统真正服务于业务，让数据真正创造价值。",
      advantage1: { title: "效率提升", desc: "上线周期缩短60%" },
      advantage2: { title: "成本优化", desc: "固定成本转为可变成本" },
      advantage3: { title: "规模增长", desc: "投保转化率与渠道能力同步提升" },
      advantage4: { title: "风险降低", desc: "数据与合规自动化管理" },
      data1: "行业经验",
      data2: "成功案例",
      data3: "专业团队",
    },
    services: {
      title: "产品与服务",
      brief:
        "我们提供全方位的数字化解决方案，满足不同行业、不同规模企业的个性化需求",
      item1: {
        title: "保险科技解决方案",
        desc: "构建统一保险业务中台，覆盖投保、核保、出单、理赔、数据监控等全流程能力。",
        tag1: "智能出单",
        tag2: "风控体系",
        tag3: "多渠道对接",
        tag4: "数据监控与报表",
      },
      item2: {
        title: "灵活用工与风控平台",
        desc: "面向物流、配送及平台企业，构建一体化灵活用工运营系统。",
        tag1: "用工人员准入",
        tag2: "保险自动绑定",
        tag3: "风控监测模型及合规",
        tag4: "数据运营",
      },
      item3: {
        title: "TPA服务",
        desc: "整合保险机构、医疗服务商、理赔资源与健康服务能力，构建标准化第三方管理平台。",
        tag1: "客户与保单管理",
        tag2: "理赔流程管理",
        tag3: "服务商管理",
        tag4: "数据运营与监控",
      },
      item4: {
        title: "行业系统定制开发",
        desc: "基于企业业务场景与管理需求，提供从咨询规划、系统设计、产品开发到运维支持的一站式行业系统交付服务。",
        tag1: "业务架构设计",
        tag2: "运维与升级",
        tag3: "数据治理体系",
        tag4: "安全与权限管理",
      },
    },
    cases: {
      title: "成功案例",
      brief:
        "我们已为金融、制造、保险、医疗等多个行业的20+企业提供了优质的数字化解决方案",
      tag1: "骑手",
      tag2: "保险行业",
      tag3: "教育行业",
      tag4: "移动支付",
      item1: {
        title: "灵活用工保险运营平台",
        desc: "为顺丰、菜鸟构建实现骑手保险实时投保，合规率提90%，人力成本降20%",
      },
      item2: {
        title: "智能理赔TPA系统",
        desc: "为保险公司打造理赔流程自动化，处理时效缩60%，客户满意度提45%",
      },
      item3: {
        title: "数字化教学平台",
        desc: "为教育机构打通课消/学情数据，教学效率提30%，续课率增25%",
      },
      item4: {
        title: "系统集成与数据中台服务",
        desc: "为支付机构提供整合多渠道数据，交易效率提50%，对账时长缩70%",
      },
    },
    contact: {
      title: "联系我们",
      brief:
        "欢迎政府机构、平台企业、保险公司及产业伙伴洽谈合作，共同打造数字化产业新生态。",
      addressTitle: "公司地址",
      addressDesc: "中国上海市普陀区大渡河路1438号信泰中心T2栋1012室",
      phoneTitle: "联系电话",
      emailTitle: "电子邮箱",
    },
    footer: {
      company: "中弘润景科技",
      companyDesc:
        "专注于企业数字化解决方案，用科技赋能企业发展，创造更大价值。",
      quickLink: "快速链接",
      serviceScope: "服务范围",
      workTime: "周一至周五 9:00-18:00",
    },
  },
  // 英文
  en: {
    logoName: "Zhonghong Runjing Technology Co.Ltd.",
    nav: {
      home: "Home",
      about: "About Us",
      services: "Services",
      cases: "Cases",
      contact: "Contact Us",
    },
    btn: { consult: "Consult Now", service: "Explore Services" },
    hero: {
      title:
        "Technology Empowers Industrial Upgrading · Data Drives Business Growth",
      desc: "Zhonghong Runjing Technology focuses on digital solutions, AI applications and enterprise-level technical services, empowering enterprises to achieve digital transformation and high-quality development.",
    },
    about: {
      title: "About Us",
      brief:
        'Founded in 2019, Zhonghong Runjing Technology is a technology enterprise with core capabilities in digital middle office and intelligent systems. Focusing on fintech, insurance tech, logistics, education and other scenarios, we help enterprises realize online business, data assetization and intelligent operation through the trinity model of "System Construction + Scenario Operation + Data Intelligence".',
      profileTitle: "Company Profile",
      profileDesc:
        "Headquartered in Shanghai, we have branches in Zhejiang, Jiangsu, Guangdong and other regions.Driven by technological innovation, we continuously provide stable, secure and scalable industry-level digital solutions.",
      missionTitle: "Corporate Mission",
      missionDesc:
        "Empower industrial upgrading with technology, drive business growth with data, make systems truly serve businesses, and let data create real value.",
      advantage1: {
        title: "Efficiency Improvement",
        desc: "60% reduction in launch cycle",
      },
      advantage2: {
        title: "Cost Optimization",
        desc: "Fixed costs converted to variable costs",
      },
      advantage3: {
        title: "Scale Growth",
        desc: "Synchronous improvement of insurance conversion rate and channel capabilities",
      },
      advantage4: {
        title: "Risk Reduction",
        desc: "Automated management of data and compliance",
      },
      data1: "Industry Experience",
      data2: "Success Cases",
      data3: "Professional Team",
    },
    services: {
      title: "Services",
      brief:
        "We provide comprehensive digital solutions to meet the personalized needs of enterprises across diverse industries and scales",
      item1: {
        title: "Insurance Technology Solutions",
        desc: "Build a unified insurance business middle office with full-process capabilities covering insurance application, underwriting, policy issuance, claims settlement, data monitoring and more.",
        tag1: "Intelligent Policy Issuance",
        tag2: "Risk Control System",
        tag3: "Multi-channel Connection",
        tag4: "Data Monitoring and Reports",
      },
      item2: {
        title: "Flexible Employment & Risk Control Platform",
        desc: "For logistics, distribution and platform enterprises, we build an integrated operational system for flexible employment.",
        tag1: "Worker Access Management",
        tag2: "Automatic Insurance Binding",
        tag3: "Risk Control Monitoring Model & Compliance",
        tag4: "Data Operation",
      },
      item3: {
        title: "TPA Service",
        desc: "Integrate insurance institutions, medical service providers, claims resources and health service capabilities to build a standardized third-party management platform.",
        tag1: "Customer & Policy Management",
        tag2: "Claim Process Management",
        tag3: "Service Provider Management",
        tag4: "Data Operation and Monitoring",
      },
      item4: {
        title: "Custom Industry System Development",
        desc: "Based on enterprises' business scenarios and management requirements, we provide one-stop industry system delivery services covering consulting planning, system design, product development and operation & maintenance support.",
        tag1: "Business Architecture Design",
        tag2: "Operation, Maintenance & Upgrade",
        tag3: "Data Governance System",
        tag4: "Security & Permission Management",
      },
    },
    cases: {
      title: "Cases",
      brief:
        "We have provided high-quality digital solutions for more than 20 enterprises in finance, manufacturing, insurance, medical care and other industries.",
      tag1: "Riders",
      tag2: "Insurance Industry",
      tag3: "Education Industry",
      tag4: "Mobile Payment",
      item1: {
        title: "Flexible Insurance Platform",
        desc: "Built for SF Express and Cainiao to realize real-time insurance application for riders, 90% compliance rate improvement and 20% labor cost reduction.",
      },
      item2: {
        title: "TPA Service",
        desc: "Built for insurance companies to automate claims process, 60% reduction in processing time and 45% improvement in customer satisfaction.",
      },
      item3: {
        title: "Digital Platform",
        desc: "Connected course consumption and learning data for educational institutions, 30% improvement in teaching efficiency and 25% in course renewal rate.",
      },
      item4: {
        title: "System Integration",
        desc: "Integrated multi-channel data for payment institutions, 50% improvement in transaction efficiency and 70% reduction in reconciliation time.",
      },
    },
    contact: {
      title: "Contact Us",
      brief:
        "Welcome government agencies, platform enterprises, insurance companies and industrial partners to discuss cooperation and jointly create a new digital industry ecosystem.",
      addressTitle: "Address",
      addressDesc:
        "Room 1012, Building T2, Xintai Center, No.1438 Daduhe Road, Putuo District, Shanghai, China",
      phoneTitle: "Phone",
      emailTitle: "Email",
    },
    footer: {
      company: "Zhonghong Runjing Technology",
      companyDesc:
        "Focus on enterprise digital solutions, empower enterprise development with technology and create greater value.",
      quickLink: "Quick Links",
      serviceScope: "Service Scope",
      workTime: "Mon-Fri 9:00-18:00",
    },
  },
};

// 5. 计算属性：根据当前语言返回对应文本（页面所有插值都用这个）
const langData = computed(() => languageMap[currentLang.value]);

// 6. 核心方法：切换语言（简体/英文）
const toggleLang = () => {
  currentLang.value = currentLang.value === "zh" ? "en" : "zh";
  // 存入本地存储
  localStorage.setItem("siteLang", currentLang.value);
  // 切换语言后关闭移动端菜单
  isMobileMenuOpen.value = false;
};

// 锚点滚动
const scrollToSection = (sectionId) => {
  const section = document.getElementById(sectionId);
  if (!section) return;
  isMobileMenuOpen.value = false;
  const navbarHeight = document.getElementById("navbar").offsetHeight;
  const targetTop = section.offsetTop - navbarHeight;
  window.scrollTo({ top: targetTop, behavior: "smooth" });
};

// 点击产品四卡片
const productDetails = (index) => {
  router.push({
    path: "/productDetails",
    query: { index: index },
  });
};
// 点击案例四卡片
const caseDetails = (index) => {
  router.push({
    path: "/caseDetails",
    query: { index: index },
  });
};

// 导航栏滚动样式变化 + 语言按钮颜色适配
onMounted(() => {
  // 添加移动端视口元标签
  const viewportMeta = document.createElement("meta");
  viewportMeta.name = "viewport";
  viewportMeta.content =
    "width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no";
  document.head.insertBefore(viewportMeta, document.head.firstChild);

  // 引入Tailwind CSS
  const tailwindScript = document.createElement("script");
  tailwindScript.src = "https://cdn.tailwindcss.com";
  document.head.appendChild(tailwindScript);

  // 引入Font Awesome
  const faLink = document.createElement("link");
  faLink.rel = "stylesheet";
  faLink.href =
    "https://cdn.jsdelivr.net/npm/font-awesome@4.7.0/css/font-awesome.min.css";
  document.head.appendChild(faLink);

  // 配置Tailwind自定义主题
  tailwindScript.onload = () => {
    window.tailwind.config = {
      theme: {
        extend: {
          colors: {
            primary: "#0A4DA2",
            secondary: "#0A4DA2",
            dark: "#1E293B",
            light: "#F8FAFC",
            gray: {
              100: "#F1F5F9",
              200: "#E2E8F0",
              300: "#CBD5E1",
              800: "#1E293B",
              900: "#0F172A",
            },
          },
          fontFamily: { sans: ["Inter", "system-ui", "sans-serif"] },
          animation: {
            "fade-in": "fadeIn 0.8s ease-in-out",
            "slide-up": "slideUp 0.6s ease-out",
            "pulse-slow": "pulse 3s cubic-bezier(0.4, 0, 0.6, 1) infinite",
          },
          keyframes: {
            fadeIn: { "0%": { opacity: "0" }, "100%": { opacity: "1" } },
            slideUp: {
              "0%": { transform: "translateY(20px)", opacity: "0" },
              "100%": { transform: "translateY(0)", opacity: "1" },
            },
          },
        },
      },
    };

    const style = document.createElement("style");
    style.type = "text/tailwindcss";
    style.textContent = `
      @layer utilities {
        .content-auto { content-visibility: auto; }
        .text-shadow { text-shadow: 0 2px 4px rgba(0,0,0,0.1); }
        .bg-gradient-tech { background: linear-gradient(135deg, #0A4DA2 0%, #165DFF 100%); }
        .hover-lift { transition: transform 0.3s ease, box-shadow 0.3s ease; }
        .hover-lift:hover { transform: translateY(-5px); box-shadow: 0 10px 25px -5px rgba(10, 77, 162, 0.1); }
      }
      body { overflow-x: hidden; margin: 0; padding: 0; }
    `;
    document.head.appendChild(style);
  };

  // 导航栏滚动监听
  const navbar = document.getElementById("navbar");
  window.addEventListener("scroll", () => {
    const isWhite = window.scrollY > 50;
    navbarBgWhite.value = isWhite;
    if (isWhite) {
      navbar.classList.remove("bg-transparent");
      navbar.classList.add("bg-white", "shadow-md");
      navbar.querySelectorAll(".md\\:flex a, #menuBtn").forEach((el) => {
        el.classList.remove("text-white");
        el.classList.add("text-dark");
      });
    } else {
      navbar.classList.add("bg-transparent");
      navbar.classList.remove("bg-white", "shadow-md");
      navbar.querySelectorAll(".md\\:flex a, #menuBtn").forEach((el) => {
        el.classList.add("text-white");
        el.classList.remove("text-dark");
      });
    }
  });
});
</script>

<style scoped>
.animate-fade-in {
  animation: fadeIn 0.8s ease-in-out;
}

.animate-slide-up {
  animation: slideUp 0.6s ease-out;
}

.productBgImgOne {
  background-image: url("@/assets/product1.png");
}
.productBgImgTwo {
  background-image: url("@/assets/product2.png");
}
.productBgImgThree {
  background-image: url("@/assets/product3.png");
}
.productBgImgFour {
  background-image: url("@/assets/product4.png");
}
.productBgImgOne,
.productBgImgTwo,
.productBgImgThree,
.productBgImgFour {
  background-size: cover; /* 图片等比缩放填满容器 */
  background-position: center; /* 居中显示 */
  background-repeat: no-repeat;
  border-radius: 0.75rem;
  position: relative;
  padding-bottom: calc(2rem + 60px);
}

/* 伪元素作为蒙层 */
.productBgImgOne::before,
.productBgImgTwo::before,
.productBgImgThree::before,
.productBgImgFour::before {
  content: "";
  position: absolute;
  inset: 0;
  background-color: rgba(0, 0, 0, 0.4);
  border-radius: 0.75rem; /* 与卡片圆角同步 */
  z-index: 0;
}

/* 将卡片内的直接子元素提升到蒙层上方 */
.productBgImgOne > *,
.productBgImgTwo > *,
.productBgImgThree > *,
.productBgImgFour > * {
  position: relative;
  z-index: 1;
}

/* 同时把标题文字改成白色，并加一点阴影增强可读性（可选） */
.productBgImgOne h3,
.productBgImgTwo h3,
.productBgImgThree h3,
.productBgImgFour h3 {
  color: #fff;
  text-shadow: 0 4px 4px rgba(0, 0, 0, 1);
}

@keyframes fadeIn {
  0% {
    opacity: 0;
  }

  100% {
    opacity: 1;
  }
}

@keyframes slideUp {
  0% {
    transform: translateY(20px);
    opacity: 0;
  }

  100% {
    transform: translateY(0);
    opacity: 1;
  }
}

:deep(.ant-btn) {
  border-radius: 0.5rem;
}

:deep(*) {
  box-sizing: border-box;
}
</style>
